import { useEffect, useRef } from 'react';
import type { MouseEvent, ReactElement } from 'react';

import CheckSolid from '@simplywallst/ui-core/icons/CheckSolid';

import type { ExtraToggleProps, EmptyStateProps } from '../Dropdown';
import { Wrapper, List, Item, Prefix } from './styled';

export interface Option {
  label: string;
  option: string;
  href?: string;
  selected?: boolean;
  level?: string;
  icon?: ReactElement;
  displayTopDivider?: boolean;
}

export interface Props {
  visible: boolean;
  options: Option[];
  align: 'left' | 'right';
  scrollToSelected: boolean;
  selected: string;
  hoveredIndex: number;
  onBlur: () => void;
  onFocus: () => void;
  onSelect: (option: string, index: number) => void;
  searchable?: boolean;
  width?: number;
  canSearch?: boolean;
  placeholder?: string;
  emptyState: (props: EmptyStateProps) => ReactElement<EmptyStateProps>;
  extraItem?: (props: ExtraToggleProps) => ReactElement<ExtraToggleProps>;
}

const Options = ({
  visible,
  options,
  align,
  hoveredIndex,
  onBlur,
  onFocus,
  onSelect,
  selected,
  scrollToSelected,
  searchable,
  width,
  extraItem,
  placeholder,
  canSearch,
  emptyState,
}: Props) => {
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    const { current } = listRef;
    if (current) {
      if (hoveredIndex > -1 && scrollToSelected) {
        const hoveredOption: HTMLLIElement | null = current.querySelector(
          '[data-is-hovering="true"]'
        );
        if (hoveredOption) {
          current.scrollTop = hoveredOption.offsetTop;
        }
      } else {
        current.scrollTop = 0;
      }
    }
  }, [hoveredIndex, scrollToSelected]);

  return (
    <Wrapper visible={visible}>
      <List
        ref={listRef}
        align={align}
        data-cy-id="dropdown-list"
        searchable={searchable}
        width={width}
      >
        {extraItem?.({ onFocus, onBlur })}
        {placeholder && !canSearch && (
          <li>
            <Item
              as="a"
              level=""
              tabIndex={-1}
              onBlur={onBlur}
              onFocus={onFocus}
              disabled={true}
            >
              {placeholder}
            </Item>
          </li>
        )}
        {options.length
          ? options.map((n, i) => (
              <li key={`dropdown-item-${n.option}-${n.label}-${i}`}>
                <Item
                  as="a"
                  level={n.level}
                  href={n.href}
                  data-cy-id="dropdown-list-item"
                  tabIndex={-1}
                  hovered={hoveredIndex === i}
                  data-is-hovering={hoveredIndex === i}
                  data-is-selected={selected === n.option}
                  onBlur={onBlur}
                  onFocus={onFocus}
                  onClick={(e: MouseEvent) => {
                    e.stopPropagation();
                    e.preventDefault();
                    selected !== n.option && onSelect(n.option, i);
                  }}
                  selected={selected === n.option}
                  disabled={n.option.length === 0}
                  displayTopDivider={n.displayTopDivider}
                >
                  {n.option.length > 0 && selected === n.option && (
                    <Prefix hovered={hoveredIndex === i}>
                      <CheckSolid />
                    </Prefix>
                  )}
                  {n.icon && selected !== n.option && (
                    <Prefix suppressedSvgColor={true}>{n.icon}</Prefix>
                  )}

                  {n.label}
                </Item>
              </li>
            ))
          : emptyState({ onFocus, onBlur })}
      </List>
    </Wrapper>
  );
};

export default Options;
